import { X } from "lucide-react-native";
import { FC, ReactNode } from "react";

import { Box, HStack, Pressable, Text } from "@/components/ui";
import { useLocalization } from "@/hooks";

interface PillItem {
  code: string;
  label: string;
  pct: number;
  prefix?: ReactNode;
}

export interface SelectionPillsProps {
  className?: string;
  disabled?: boolean;
  hasDistribution: boolean;
  items: PillItem[];
  onOpenWeights?: () => void;
  onRemove?: (code: string) => void;
  otherPct: number;
  readonly?: boolean;
}

const PILL_STYLE = {
  alignItems: "center" as const,
  gap: 6,
  paddingBottom: 4,
  paddingLeft: 10,
  paddingRight: 10,
  paddingTop: 4,
};

export const SelectionPills: FC<SelectionPillsProps> = ({
  className = "",
  disabled = false,
  hasDistribution,
  items,
  onOpenWeights,
  onRemove,
  otherPct,
  readonly = false,
}) => {
  const { i18n } = useLocalization();
  const interactionDisabled = disabled || readonly;

  return (
    <HStack
      className={`border-t border-outline-200 ${className}`}
      style={{ flexWrap: "wrap", gap: 8, padding: 16, minHeight: 52 }}
    >
      {items.map((item) => (
        <HStack
          className="rounded-full bg-background-50 border border-outline-200"
          key={item.code}
          style={PILL_STYLE}
        >
          {item.prefix && item.prefix}
          <Text className="text-xs text-typography-600">{item.label}</Text>
          {hasDistribution && (
            <>
              <Box
                className="bg-outline-200"
                style={{ width: 1, height: 12, flexShrink: 0 }}
              />
              {!interactionDisabled && onOpenWeights ? (
                <Pressable onPress={onOpenWeights}>
                  <Text className="text-xs font-semibold text-primary-600">
                    {i18n.numberToPercentage(item.pct, { precision: 0 })}
                  </Text>
                </Pressable>
              ) : (
                <Text className="text-xs font-semibold text-primary-600">
                  {i18n.numberToPercentage(item.pct, { precision: 0 })}
                </Text>
              )}
            </>
          )}
          {!readonly && onRemove && (
            <Pressable
              onPress={() => onRemove(item.code)}
              className="flex-row items-center"
            >
              <X color="rgb(var(--color-typography-400))" size={12} />
            </Pressable>
          )}
        </HStack>
      ))}
      {otherPct > 0 && hasDistribution && (
        <HStack
          className="rounded-full bg-background-50 border border-dashed border-outline-200"
          style={PILL_STYLE}
        >
          <Text className="text-xs text-typography-400">
            {i18n.t("misc.otherValues")}
          </Text>
          <Box
            className="bg-outline-200"
            style={{ width: 1, height: 12, flexShrink: 0 }}
          />
          <Text className="text-xs font-semibold text-typography-400">
            {i18n.numberToPercentage(otherPct, { precision: 0 })}
          </Text>
        </HStack>
      )}
    </HStack>
  );
};
